import React from 'react';
import type { GstRevenueChartPoint, GstRevenueBarItem, GstRevenueStackedBar, GstRevenueStatCard } from '../data/types';
import { coverFonts, overviewColors, ptToPx, space } from '../data/designTokens';
import { shortMonthLabel } from '../data/monthLabels';

// ════════════════════════════════════════════════════════════════
//  GST chart primitives — shared between GstRevenue and GstExpenses.
//  Screen* components render via HTML <svg>.
// ════════════════════════════════════════════════════════════════

const SF = coverFonts.screen;
export const DASH = '—';

// ── Design tokens ───────────────────────────────────────────────
export const statStrip = {
    border: 1,
    borderColor: '#E8E8E8',
    radius: 6,
    pad: 16,
    padX: 20,
    labelSize: 7.5,
    labelSpacing: 0.75,
    valueSize: 16.5,
    subSize: 7.5,
} as const;

export const tbl = {
    headerBg: '#F7F8F9',
    headerSize: 7.5,
    headerWeight: 600,
    headerColor: '#6B7080',
    headerLetterSpacing: 0.6,
    headerPadY: 5,
    headerPadX: 3,
    headerBorder: 1,
    headerBorderColor: overviewColors.border,
    bodySize: 6,
    // Smaller mono size + tighter horizontal padding so exact amounts in the
    // 12 month columns fit on a single line without wrapping.
    bodyMonoSize: 5.25,
    bodyPadY: 5,
    bodyPadX: 2,
    bodyColor: '#333333',
    bodyBoldColor: '#1a1a1a',
    rowBorder: 0.5,
    rowBorderColor: overviewColors.border,
    catBg: '#F7F8F9',
    catSize: 7.5,
    catWeight: 700,
    catColor: '#333333',
    catLetterSpacing: 0.6,
    totBorderTop: 1,
    totBorderColor: overviewColors.border,
    outerRadius: 8,
    outerBorder: 1,
    // Narrow label columns: classification labels and customer/vendor names
    // wrap onto a second line so the 12 month columns get enough width to
    // show exact amounts on a single line.
    monthTable: { classification: 9, month: 7 },
    b2b: { name: 10, month: 7 },
} as const;

export const chartCard = {
    border: 0.5,
    borderColor: '#E8E8E8',
    radius: 4.5,
    pad: 10.5,
    padX: 12,
    titleSize: 9,
    valueSize: 12,
    valueSmallSize: 10.5,
    subSize: 7.5,
    gridGap: 10.5,
    legendDotSize: 4.5,
    legendSize: 6.75,
    legendGap: 9,
    svgWidth: 220,
    svgHeight: 105,
    hbarLabelWidth: 82,
    hbarHeight: 10.5,
    hbarValWidth: 62,
    hbarGap: 3.75,
} as const;

export const chartColors = {
    ink: '#1a1a1a',
    pos: '#2D8B75',
    posLight: '#6BC4A6', // secondary series in green-toned dual charts
    posSoft: '#D0E8E2', // non-recurring share in green stacked bars
    neg: '#D94545',
    negSoft: '#F5A3A3', // secondary series / non-recurring share in red-toned charts
    purple: '#6366F1',
    purpleLight: '#B8C4FF',
    cyan: '#06B6D4',
    amber: '#F59E0B',
    gridLine: '#f3f3f3',
    axisLabel: '#aaaaaa',
    gradientStops: ['#1a6b58', '#2D8B75', '#6BC4A6', '#8ED4BA', '#B8E4D4', '#D0E8E2'],
} as const;

export const toneFg = (tone?: string) =>
    tone === 'red' ? '#D94545' : tone === 'green' ? '#2D8B75' : tone === 'orange' ? '#E6912A' : overviewColors.ink;

// ── Value formatting helpers ────────────────────────────────────
// Compact data-label for chart values that are already in ₹ Lacs.
// Small values keep one decimal (₹1.8L) so they match the stat cards;
// whole-Lac rounding made ₹1.75 Lacs read as "₹2L"/"₹1L".
export const fmtLacLabel = (v: number): string =>
    Math.abs(v) >= 100 ? `₹${(v / 100).toFixed(1)}Cr` : Math.abs(v) >= 10 ? `₹${Math.round(v)}L` : `₹${v.toFixed(1)}L`;

// ── Chart helpers ───────────────────────────────────────────────
export const linePoints = (data: GstRevenueChartPoint[], w: number, h: number, padL: number, padT: number, padB: number) => {
    if (data.length === 0) return [];
    // Filter to finite values before min/max — a single NaN/Infinity makes
    // min/max NaN, poisoning `range` and every y-coord, which corrupts the SVG
    // points string in the LIVE ScreenLineChart (skill rule 25).
    const vals = data.map((d) => d.value).filter((v) => Number.isFinite(v));
    const min = vals.length ? Math.min(...vals) : 0;
    const max = vals.length ? Math.max(...vals) : 0;
    const range = max - min || 1;
    return data.map((d, i) => {
        const v = Number.isFinite(d.value) ? d.value : min;
        return {
            x: padL + (i / Math.max(1, data.length - 1)) * (w - padL - 10),
            y: padT + (1 - (v - min) / range) * (h - padT - padB),
            label: d.label,
            value: v,
        };
    });
};
export const pointsToString = (pts: Array<{ x: number; y: number }>): string => pts.map((p) => `${p.x},${p.y}`).join(' ');

// ── Month axis labels ───────────────────────────────────────────
export const ScreenAxisMonth: React.FC<{ x: number; y: number; label: string }> = ({ x, y, label }) => (
    <text textAnchor="end" transform={`translate(${x}, ${y}) rotate(-40)`} fontSize={5} fill={chartColors.axisLabel}>
        {shortMonthLabel(label)}
    </text>
);

// ── Shared cell style builders ──────────────────────────────────
export const sHdrCell = (flex: number, align: 'left' | 'right' = 'left', last = false): React.CSSProperties => ({
    flex,
    fontSize: ptToPx(tbl.headerSize),
    fontFamily: SF.sans,
    fontWeight: tbl.headerWeight,
    color: tbl.headerColor,
    letterSpacing: `${tbl.headerLetterSpacing}px`,
    textTransform: 'uppercase',
    textAlign: align,
    padding: `${ptToPx(tbl.headerPadY)}px ${ptToPx(tbl.headerPadX)}px`,
    minWidth: 0,
    borderRight: last ? undefined : `${ptToPx(tbl.rowBorder)}px solid ${tbl.rowBorderColor}`,
});
export const sBodyCell = (
    flex: number,
    opts: { align?: 'left' | 'right'; mono?: boolean; bold?: boolean; wrap?: boolean; last?: boolean } = {},
): React.CSSProperties => ({
    flex,
    fontSize: ptToPx(opts.mono ? tbl.bodyMonoSize : tbl.bodySize),
    fontFamily: opts.mono ? SF.mono : SF.sans,
    fontWeight: opts.bold ? 700 : 400,
    color: opts.bold ? tbl.bodyBoldColor : tbl.bodyColor,
    textAlign: opts.align ?? 'left',
    padding: `${ptToPx(tbl.bodyPadY)}px ${ptToPx(tbl.bodyPadX)}px`,
    minWidth: 0,
    borderRight: opts.last ? undefined : `${ptToPx(tbl.rowBorder)}px solid ${tbl.rowBorderColor}`,
    ...(opts.wrap ? { wordBreak: 'break-word' as const } : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }),
});

// ── Shared month-column table ────────────────────────────────────
// One table for all four GST tables (GSTR-3B / GSTR-2 classification tables and
// the B2B customer / vendor tables) — they share the same bordered box, header
// row and month-column layout. `variant` picks the label-column width:
//   'classification' → category-header rows (Revenue / Purchases / …)
//   'b2b'            → wider name column + isTotal rows with a top border.
export type MonthTableRow = {
    label: string;
    category?: string; // group-header row (rendered as a tinted band, no amounts)
    bold?: boolean;
    isTotal?: boolean; // b2b total row — top border + bold values
    amounts?: Record<string, string>; // keyed by header label
};

export const ScreenMonthTable: React.FC<{
    variant: 'classification' | 'b2b';
    title?: string;
    subtitle?: string;
    firstColLabel: string;
    headers: Array<{ key: string; label: string }>;
    rows: MonthTableRow[];
    footer?: string;
}> = ({ variant, title, subtitle, firstColLabel, headers, rows, footer }) => {
    const firstColFlex = variant === 'b2b' ? tbl.b2b.name : tbl.monthTable.classification;
    const monthFlex = variant === 'b2b' ? tbl.b2b.month : tbl.monthTable.month;
    return (
        <div>
            {title ? (
                <div
                    style={{
                        fontSize: ptToPx(12),
                        fontFamily: SF.sans,
                        fontWeight: 700,
                        color: overviewColors.ink,
                        marginBottom: ptToPx(subtitle ? 4.5 : 7.5),
                    }}
                >
                    {title}
                </div>
            ) : null}
            {subtitle ? (
                <div style={{ fontSize: ptToPx(9), fontFamily: SF.sans, color: '#888888', marginBottom: ptToPx(10.5) }}>{subtitle}</div>
            ) : null}
            <div
                style={{
                    border: `${tbl.outerBorder}px solid ${overviewColors.border}`,
                    borderRadius: ptToPx(tbl.outerRadius),
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        background: tbl.headerBg,
                        borderBottom: `${ptToPx(tbl.headerBorder)}px solid ${tbl.headerBorderColor}`,
                    }}
                >
                    <span style={sHdrCell(firstColFlex)}>{firstColLabel}</span>
                    {headers.map((h, hi) => (
                        <span key={h.key} style={sHdrCell(monthFlex, 'right', hi === headers.length - 1)}>
                            {h.label}
                        </span>
                    ))}
                </div>
                {rows.map((row, i) => {
                    if (row.category && !row.amounts)
                        return (
                            <div key={i} style={{ background: tbl.catBg, padding: `${ptToPx(tbl.bodyPadY)}px ${ptToPx(tbl.bodyPadX)}px` }}>
                                <span
                                    style={{
                                        fontSize: ptToPx(tbl.catSize),
                                        fontFamily: SF.sans,
                                        fontWeight: tbl.catWeight,
                                        letterSpacing: `${tbl.catLetterSpacing}px`,
                                        textTransform: 'uppercase',
                                        color: tbl.catColor,
                                    }}
                                >
                                    {row.category}
                                </span>
                            </div>
                        );
                    return (
                        <div
                            key={i}
                            style={{
                                display: 'flex',
                                borderBottom: `${ptToPx(tbl.rowBorder)}px solid ${tbl.rowBorderColor}`,
                                ...(row.isTotal ? { borderTop: `${ptToPx(tbl.totBorderTop)}px solid ${tbl.totBorderColor}` } : {}),
                            }}
                        >
                            <span style={sBodyCell(firstColFlex, { bold: row.bold, wrap: true })}>{row.label}</span>
                            {headers.map((h, hi) => (
                                <span
                                    key={h.key}
                                    style={sBodyCell(monthFlex, {
                                        align: 'right',
                                        mono: true,
                                        bold: row.isTotal,
                                        last: hi === headers.length - 1,
                                    })}
                                >
                                    {row.amounts?.[h.key] ?? DASH}
                                </span>
                            ))}
                        </div>
                    );
                })}
            </div>
            {footer ? (
                <div style={{ fontSize: ptToPx(7.5), fontFamily: SF.sans, color: '#bbbbbb', marginTop: ptToPx(4) }}>{footer}</div>
            ) : null}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════
//                            S C R E E N
// ════════════════════════════════════════════════════════════════

export const ScreenStatStrip: React.FC<{ stats: GstRevenueStatCard[] }> = ({ stats }) => (
    <div
        style={{
            display: 'flex',
            border: `${ptToPx(statStrip.border)}px solid ${statStrip.borderColor}`,
            borderRadius: ptToPx(statStrip.radius),
            overflow: 'hidden',
            marginBottom: ptToPx(space.sp8),
        }}
    >
        {stats.map((s, i) => (
            <div
                key={i}
                style={{
                    flex: 1,
                    padding: `${ptToPx(statStrip.pad)}px ${ptToPx(statStrip.padX)}px`,
                    borderRight: i < stats.length - 1 ? `${ptToPx(statStrip.border)}px solid ${statStrip.borderColor}` : 'none',
                }}
            >
                <div
                    style={{
                        fontSize: ptToPx(statStrip.labelSize),
                        fontFamily: SF.sans,
                        fontWeight: 600,
                        letterSpacing: `${statStrip.labelSpacing}px`,
                        textTransform: 'uppercase',
                        color: '#888888',
                        marginBottom: ptToPx(6),
                    }}
                >
                    {s.label}
                </div>
                <div style={{ fontSize: ptToPx(statStrip.valueSize), fontFamily: SF.sans, fontWeight: 700, color: toneFg(s.tone) }}>
                    {s.value}
                </div>
                {s.sub ? (
                    <div style={{ fontSize: ptToPx(statStrip.subSize), fontFamily: SF.sans, color: '#aaaaaa', marginTop: ptToPx(2) }}>
                        {s.sub}
                    </div>
                ) : null}
            </div>
        ))}
    </div>
);

export const ScreenChartCard: React.FC<{
    title: string;
    value?: string;
    valueSmall?: boolean;
    sub?: string;
    children: React.ReactNode;
}> = ({ title, value, valueSmall, sub, children }) => (
    <div
        style={{
            flex: 1,
            border: `${ptToPx(chartCard.border)}px solid ${chartCard.borderColor}`,
            borderRadius: ptToPx(chartCard.radius),
            padding: `${ptToPx(chartCard.pad)}px ${ptToPx(chartCard.padX)}px`,
        }}
    >
        <div
            style={{
                fontSize: ptToPx(chartCard.titleSize),
                fontFamily: SF.sans,
                fontWeight: 700,
                color: chartColors.ink,
                marginBottom: ptToPx(1.5),
            }}
        >
            {title}
        </div>
        {value ? (
            // Value on its own line, description below — matches the PDF card.
            <div style={{ marginBottom: ptToPx(4.5) }}>
                <div
                    style={{
                        fontSize: ptToPx(valueSmall ? chartCard.valueSmallSize : chartCard.valueSize),
                        fontFamily: SF.mono,
                        fontWeight: 500,
                        color: chartColors.ink,
                    }}
                >
                    {value}
                </div>
                {sub ? (
                    <div style={{ fontSize: ptToPx(chartCard.subSize), fontFamily: SF.sans, color: '#aaaaaa', marginTop: ptToPx(2) }}>
                        {sub}
                    </div>
                ) : null}
            </div>
        ) : null}
        {children}
    </div>
);

export const ScreenLegend: React.FC<{ items: Array<{ label: string; color: string }> }> = ({ items }) => (
    <div style={{ display: 'flex', gap: ptToPx(chartCard.legendGap), marginTop: ptToPx(4.5) }}>
        {items.map((it, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: ptToPx(3) }}>
                <div
                    style={{
                        width: ptToPx(chartCard.legendDotSize),
                        height: ptToPx(chartCard.legendDotSize),
                        borderRadius: '50%',
                        background: it.color,
                    }}
                />
                <span style={{ fontSize: ptToPx(chartCard.legendSize), fontFamily: SF.sans, color: '#777777' }}>{it.label}</span>
            </div>
        ))}
    </div>
);

// Screen chart components use HTML <svg> — labels render natively
export const ScreenLineChart: React.FC<{ data: GstRevenueChartPoint[]; color?: string }> = ({ data, color = chartColors.ink }) => {
    const w = 295;
    const h = 140;
    const pts = linePoints(data, w, h, 20, 10, 25);
    if (pts.length < 2) return null;
    const areaPath = `M${pts[0].x},${pts[0].y} ${pts
        .slice(1)
        .map((p) => `L${p.x},${p.y}`)
        .join(' ')} L${pts[pts.length - 1].x},${h - 15} L${pts[0].x},${h - 15} Z`;
    const markers = [0, Math.floor(data.length / 2), data.length - 1];
    return (
        <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', display: 'block' }}>
            <path d={areaPath} fill={color} opacity={0.05} />
            <polyline
                points={pts.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke={color}
                strokeWidth={1.5}
                strokeLinejoin="round"
            />
            {pts.map((p, i) => (markers.includes(i) ? <circle key={i} cx={p.x} cy={p.y} r={2} fill={color} /> : null))}
            {/* Data label on every point with data — alternating above/below the line so neighbours don't collide */}
            {pts.map((p, i) =>
                p.value > 0 ? (
                    <text
                        key={`v${i}`}
                        x={p.x}
                        y={i % 2 === 0 ? p.y - 5 : p.y + 10}
                        fontSize={5}
                        fill={color}
                        textAnchor="middle"
                        fontWeight={600}
                        // White outline behind the glyphs so the line stroke never cuts through the label
                        stroke="#FFFFFF"
                        strokeWidth={2}
                        paintOrder="stroke"
                        strokeLinejoin="round"
                    >
                        {fmtLacLabel(p.value)}
                    </text>
                ) : null,
            )}
            {pts.map((p, i) => (
                <ScreenAxisMonth key={`a${i}`} x={p.x} y={h - 10} label={p.label} />
            ))}
        </svg>
    );
};

export const ScreenGrowthBars: React.FC<{ data: GstRevenueChartPoint[] }> = ({ data }) => {
    const w = 295;
    const h = 140;
    if (data.length === 0) return null;
    const maxAbs = Math.max(...data.map((d) => Math.abs(d.value)), 1);
    const midY = h * 0.5;
    const barW = (w - 30) / data.length - 2;
    return (
        <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', display: 'block' }}>
            <line x1={15} y1={midY} x2={w - 5} y2={midY} stroke="#E0E0E0" strokeWidth={0.5} />
            {data.map((d, i) => {
                const x = 20 + i * ((w - 35) / data.length);
                const barH = (Math.abs(d.value) / maxAbs) * (midY - 15);
                const isPos = d.value >= 0;
                return (
                    <g key={i}>
                        <rect
                            x={x}
                            y={isPos ? midY - barH : midY}
                            width={barW}
                            height={barH}
                            rx={1}
                            fill={isPos ? chartColors.pos : chartColors.neg}
                        />
                        {/* Rounded like the PDF label so both renders show the same value */}
                        <text
                            x={x + barW / 2}
                            y={isPos ? midY - barH - 4 : midY + barH + 8}
                            fontSize={5}
                            fill={isPos ? chartColors.pos : chartColors.neg}
                            textAnchor="middle"
                        >{`${Math.round(d.value)}%`}</text>
                        <ScreenAxisMonth x={x + barW / 2} y={h - 10} label={d.label} />
                    </g>
                );
            })}
        </svg>
    );
};

// barColor: solid fill for every bar; when omitted the green gradient ramp is
// used (rank-ordered items, e.g. state/customer breakdowns).
export const ScreenHBars: React.FC<{ items: GstRevenueBarItem[]; barColor?: string }> = ({ items, barColor }) => (
    <div style={{ marginTop: ptToPx(6) }}>
        {items.map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: ptToPx(4.5), marginBottom: ptToPx(chartCard.hbarGap) }}>
                <span
                    style={{
                        width: ptToPx(chartCard.hbarLabelWidth),
                        fontSize: ptToPx(6.75),
                        fontFamily: SF.sans,
                        color: '#555555',
                        textAlign: 'left',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {item.label}
                </span>
                <div
                    style={{
                        flex: 1,
                        height: ptToPx(chartCard.hbarHeight),
                        background: '#f5f5f5',
                        borderRadius: ptToPx(1.5),
                        overflow: 'hidden',
                    }}
                >
                    <div
                        style={{
                            width: `${item.percent}%`,
                            height: '100%',
                            background: barColor ?? chartColors.gradientStops[Math.min(i, chartColors.gradientStops.length - 1)],
                            borderRadius: ptToPx(1.5),
                        }}
                    />
                </div>
                <span style={{ width: ptToPx(chartCard.hbarValWidth), fontSize: ptToPx(6.75), fontFamily: SF.mono, color: '#444444' }}>
                    {item.value}
                </span>
            </div>
        ))}
    </div>
);

export const ScreenStackedBars: React.FC<{ data: GstRevenueStackedBar[]; secondaryFill?: string; height?: number }> = ({
    data,
    secondaryFill = chartColors.posSoft,
    height = 150,
}) => {
    const w = 295;
    const h = height;
    if (data.length === 0) return null;
    const barW = (w - 30) / data.length - 2;
    // 100% stacked: every month fills the full height and the green share is
    // the recurring %. Absolute-value scaling made bars look broken when month
    // totals varied wildly, so the chart shows the split, not the volume.
    const fullH = h - 40;
    const baseTop = 16;
    return (
        <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', display: 'block' }}>
            {data.map((d, i) => {
                const total = d.recurring + d.nonRecurring;
                const x = 15 + i * ((w - 30) / data.length);
                const recH = total > 0 ? (d.recurring / total) * fullH : 0;
                const recPct = total > 0 ? Math.round((d.recurring / total) * 100) : 0;
                return (
                    <g key={i}>
                        {/* Native SVG tooltip on hover */}
                        <title>{`${shortMonthLabel(d.label)} — Recurring ${recPct}% · Non-recurring ${100 - recPct}%`}</title>
                        {/* Zero-total month → no bar/percent (0/0 = NaN heights), but the month label below still renders */}
                        {total > 0 && (
                            <>
                                <rect x={x} y={baseTop} width={barW} height={recH} rx={1} fill={chartColors.pos} />
                                <rect x={x} y={baseTop + recH} width={barW} height={fullH - recH} rx={1} fill={secondaryFill} />
                                {/* Recurring % label per bar — tilted like the month axis to avoid overlap */}
                                <text
                                    textAnchor="start"
                                    transform={`translate(${x + barW / 2}, ${baseTop - 5}) rotate(-40)`}
                                    fontSize={5}
                                    fill={chartColors.pos}
                                    fontWeight={600}
                                >
                                    {recPct}%
                                </text>
                            </>
                        )}
                        <ScreenAxisMonth x={x + barW / 2} y={h - 10} label={d.label} />
                    </g>
                );
            })}
        </svg>
    );
};

// Existing vs New as two simple lines with a value at every dot —
// replaces the stacked bars per design feedback (the two trends read
// separately; Existing labels sit above its line, New below).
export const ScreenDualAreaChart: React.FC<{
    existing: GstRevenueChartPoint[];
    newData: GstRevenueChartPoint[];
    primaryColor?: string;
    secondaryColor?: string;
    height?: number;
}> = ({ existing, newData, primaryColor = chartColors.pos, secondaryColor = chartColors.posLight, height = 155 }) => {
    const w = 295;
    const h = height;
    const n = Math.min(existing.length, newData.length);
    if (n === 0) return null;
    const maxVal = Math.max(...existing.slice(0, n).map((p) => p.value), ...newData.slice(0, n).map((p) => p.value), 1);
    const padL = 18;
    const padT = 14;
    const padB = 25;
    const usableW = w - padL - 12;
    const usableH = h - padT - padB;
    const xAt = (i: number) => padL + (i / (n - 1 || 1)) * usableW;
    const yAt = (v: number) => padT + (1 - Math.max(v, 0) / maxVal) * usableH;
    const seriesList = [
        { pts: existing.slice(0, n), color: primaryColor, above: true },
        { pts: newData.slice(0, n), color: secondaryColor, above: false },
    ];
    return (
        <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', display: 'block' }}>
            {seriesList.map((s, si) => (
                <g key={si}>
                    <polyline
                        points={s.pts.map((p, i) => `${xAt(i)},${yAt(p.value)}`).join(' ')}
                        fill="none"
                        stroke={s.color}
                        strokeWidth={1.5}
                        strokeLinejoin="round"
                    />
                    {s.pts.map((p, i) => (
                        <g key={`d${i}`}>
                            {/* Native SVG tooltip on hover */}
                            <title>{`${shortMonthLabel(existing[i].label)} — Existing ${fmtLacLabel(existing[i].value)} · New ${fmtLacLabel(
                                newData[i].value,
                            )}`}</title>
                            <circle cx={xAt(i)} cy={yAt(p.value)} r={2} fill={s.color} />
                        </g>
                    ))}
                </g>
            ))}
            {/* Labels drawn in a second pass so neither series' line cuts through them.
                Alternating rows so neighbours don't collide; near-baseline dots always
                label above so text never hits the month axis */}
            {seriesList.map((s, si) => (
                <g key={`l${si}`}>
                    {s.pts.map((p, i) =>
                        p.value > 0 ? (
                            <text
                                key={`v${i}`}
                                x={xAt(i)}
                                y={
                                    s.above
                                        ? yAt(p.value) - (i % 2 === 0 ? 9 : 4)
                                        : yAt(p.value) > padT + usableH - 16
                                        ? yAt(p.value) - (i % 2 === 0 ? 4 : 9)
                                        : yAt(p.value) + (i % 2 === 0 ? 8 : 13)
                                }
                                textAnchor="middle"
                                fontSize={5}
                                fill={s.color}
                                fontWeight={600}
                                // White outline behind the glyphs so line strokes never cut through the label
                                stroke="#FFFFFF"
                                strokeWidth={2}
                                paintOrder="stroke"
                                strokeLinejoin="round"
                            >
                                {fmtLacLabel(p.value)}
                            </text>
                        ) : null,
                    )}
                </g>
            ))}
            {existing.slice(0, n).map((p, i) => (
                <ScreenAxisMonth key={`a${i}`} x={xAt(i)} y={h - 10} label={p.label} />
            ))}
        </svg>
    );
};
